import { useUrlGenerator } from '@folklore/routes';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link } from 'wouter';

import useOrder from '../../../hooks/useOrder';

import { useApi } from '../../../contexts/ApiContext';
import { useMenus } from '../../../contexts/SiteContext';
import { useAddToast } from '../../../contexts/ToastContext';
import { useUser } from '../../../contexts/UserContext';
import StyledButton from '../../buttons/StyledButton';
import PaymentForm from '../../forms/Payment';
import PageMeta from '../../partials/PageMeta';
import Sidebar from '../../partials/Sidebar';

import styles from '../../../../styles/pages/pro.module.css';

const propTypes = {
    page: PropTypes.shape({}),
    className: PropTypes.string,
};

const PaymentPage = ({ page, className = null }) => {
    const {
        order: initialOrder = null,
        paymentCreateUrl = null,
        paymentConfirmUrl = null,
    } = page || {};

    const { id: orderId = null } = initialOrder || {};
    const { data: order = null } = useOrder(orderId);
    const { price = null, paid = false, purchases = null } = order || {};

    const {
        price: amount = null,
        total = null,
        currency = 'CAD',
        tps = null,
        tvq = null,
    } = price || {};

    const api = useApi();
    const route = useUrlGenerator();
    const user = useUser();
    const { pro } = useMenus();
    const addToast = useAddToast();
    const { emailVerified = false } = user || {};

    const [success, setSuccess] = useState(paid);
    const [loading, setLoading] = useState(false);

    const onPaymentComplete = useCallback(() => {
        addToast({
            id: 'payment-complete',
            title: <FormattedMessage defaultMessage="Payment complete" description="Toast title" />,
            theme: 'success',
            description: (
                <FormattedMessage
                    defaultMessage="Thank you! We will contact you shortly about your order."
                    description="Toast description"
                />
            ),
        });
    }, [addToast]);

    const onOrderComplete = useCallback(() => {
        addToast({
            id: 'order-complete',
            title: <FormattedMessage defaultMessage="Order complete" description="Toast title" />,
            theme: 'success',
            description: (
                <FormattedMessage
                    defaultMessage="Thank you! We will verify your order and contact you shortly about payment."
                    description="Toast description"
                />
            ),
        });
    }, [addToast]);

    const onSuccess = useCallback(() => {
        setLoading(false);
        setSuccess(true);
        onPaymentComplete();
    }, [setSuccess, setLoading, onPaymentComplete]);

    useEffect(() => {
        if (paid) {
            setSuccess(true);
            setLoading(false);
        }
    }, [paid, setSuccess, setLoading]);

    const paymentsApi = useMemo(
        () => ({
            createIntent: (data) => api.purchases.createIntent(paymentCreateUrl, data),
            confirmIntent: (...args) => api.purchases.confirmIntent(paymentConfirmUrl, ...args),
        }),
        [api, paymentCreateUrl, paymentConfirmUrl],
    );

    if (purchases !== null) {
        return (
            <div
                className={classNames([
                    styles.container,
                    {
                        [className]: className !== null,
                    },
                ])}
            >
                <Sidebar
                    className={styles.sidebar}
                    menu={pro}
                    title={
                        <FormattedMessage defaultMessage="Thank you!" description="Page title" />
                    }
                >
                    <p>
                        <FormattedMessage
                            defaultMessage="This order has already been purchased."
                            description="Section paragraph"
                        />
                    </p>
                    <div className={classNames(styles.section, styles.centered)}>
                        <StyledButton href={route('pro.orders')} className={styles.back}>
                            <FormattedMessage
                                defaultMessage="Go to my orders"
                                description="Section paragraph"
                            />
                        </StyledButton>
                    </div>
                </Sidebar>
            </div>
        );
    }

    if (parseFloat(total, 10) === 0) {
        return (
            <div
                className={classNames([
                    styles.container,
                    {
                        [className]: className !== null,
                    },
                ])}
            >
                <Sidebar
                    className={styles.sidebar}
                    menu={pro}
                    title={
                        <FormattedMessage defaultMessage="Thank you!" description="Page title" />
                    }
                >
                    <p>
                        <FormattedMessage
                            defaultMessage="This order is free."
                            description="Section paragraph"
                        />
                    </p>
                    <div className={classNames(styles.section, styles.centered)}>
                        <StyledButton href={route('pro.orders')} className={styles.back}>
                            <FormattedMessage
                                defaultMessage="Go to my orders"
                                description="Section paragraph"
                            />
                        </StyledButton>
                    </div>
                </Sidebar>
            </div>
        );
    }

    return (
        <div
            className={classNames([
                styles.container,
                {
                    [className]: className !== null,
                },
            ])}
        >
            <PageMeta page={page} />
            <Sidebar
                className={styles.sidebar}
                menu={pro}
                title={
                    success ? (
                        <FormattedMessage defaultMessage="Thank you!" description="Page title" />
                    ) : (
                        <FormattedMessage
                            defaultMessage="Book your projection"
                            description="Page title"
                        />
                    )
                }
            >
                {success ? (
                    <div className={styles.section}>
                        <p className={styles.label}>
                            <FormattedMessage
                                defaultMessage="Thank you for your purchase. You will be contacted shortly by our staff about the Semaine du Doc."
                                description="Section paragraph"
                            />
                        </p>
                        <StyledButton href={route('pro.orders')}>
                            <FormattedMessage
                                defaultMessage="Go back to your orders"
                                description="Section paragraph"
                            />
                        </StyledButton>
                    </div>
                ) : (
                    <div className={styles.form}>
                        <div className={styles.box}>
                            <p className={styles.label}>
                                <FormattedMessage
                                    defaultMessage="Payment summary"
                                    description="Summary label"
                                />
                            </p>
                            <div className={styles.summary}>
                                <div className={styles.price}>{`Montant: ${amount} $`}</div>
                                <div className={styles.price}>{`TPS: ${tps} $`}</div>
                                <div className={styles.price}>{`TVQ: ${tvq} $`}</div>
                                <div className={styles.price}>{`Total: ${total} $`}</div>
                            </div>
                        </div>
                        <div
                            className={styles.formFrame}
                            title={
                                <FormattedMessage
                                    defaultMessage="Payment information"
                                    description="Form heading"
                                />
                            }
                        >
                            <PaymentForm
                                className={styles.formContainer}
                                api={paymentsApi}
                                amount={total}
                                currency={currency}
                                onSuccess={onSuccess}
                                disabled={paid || !emailVerified || loading}
                            />
                        </div>
                        <Link to={route('pro.orders')} onClick={onOrderComplete}>
                            <div className={styles.back}>
                                <FormattedMessage
                                    defaultMessage="I want to pay by another means / cannot pay with a credit card."
                                    description="Button label"
                                />
                            </div>
                        </Link>
                    </div>
                )}
            </Sidebar>
        </div>
    );
};

PaymentPage.propTypes = propTypes;

export default PaymentPage;
